import sapHorizon from 'fundamental-styles/dist/js/theming/sap_horizon';
import sapHorizonDark from 'fundamental-styles/dist/js/theming/sap_horizon_dark';
import sapHorizonHcb from 'fundamental-styles/dist/js/theming/sap_horizon_hcb';
import sapHorizonHcw from 'fundamental-styles/dist/js/theming/sap_horizon_hcw';
import React, { ReactNode, useEffect } from 'react';

import { setTheme } from '@ui5/webcomponents-base/dist/config/Theme';
import { ThemeProvider as UI5ThemeProvider } from '@ui5/webcomponents-react';
import { useStylesheet } from '@ui5/webcomponents-react-base';
import '@ui5/webcomponents-react/dist/Assets';

import 'fundamental-styles/dist/fundamental-styles.css';
import horizonStyles from './themes/sap_horizon.css?inline';
import horizonOverrides from './themes/sap_horizon.json';
import horizonDarkOverrides from './themes/sap_horizon_dark.json';
import horizonHcwOverrides from './themes/sap_horizon_hcw.json';
import sbaColors from './variables/sba_colors.json';
import sbaDimensions from './variables/sba_dimensions.json';
import sbaLevels from './variables/sba_levels.json';
import sbaTransitions from './variables/sba_transitions.json';

export type Theme =
  | 'sap_horizon'
  | 'sap_horizon_dark'
  | 'sap_horizon_hcb'
  | 'sap_horizon_hcw';

const getOverrides = (theme: Theme) => {
  switch (theme) {
    case 'sap_horizon':
      return horizonOverrides;
    case 'sap_horizon_dark':
      return horizonDarkOverrides;
    case 'sap_horizon_hcw':
      return horizonHcwOverrides;
    default:
      return null;
  }
};

const getFundamentals = (theme: Theme) => {
  switch (theme) {
    case 'sap_horizon_dark':
      return sapHorizonDark;
    case 'sap_horizon_hcb':
      return sapHorizonHcb;
    case 'sap_horizon_hcw':
      return sapHorizonHcw;
    case 'sap_horizon':
    default:
      return sapHorizon;
  }
};

const stylesToString = (theme: Theme, list: Record<string, string>) =>
  `.sap-build-apps-${theme}{${Object.entries(list)
    .map(([key, value]) => {
      return `${key}: ${value};`;
    })
    .join(' ')}}`;

interface ThemeProviderProps {
  children: ReactNode;
  theme: Theme;
}

const stylePrefix = 'sap-build-apps-';

function OverriddenStyles({ theme }: { theme: Theme }) {
  const overrides = getOverrides(theme);

  const combinedStyles = [
    getFundamentals(theme).cssSource,
    stylesToString(theme, sbaColors),
    stylesToString(theme, sbaDimensions),
    stylesToString(theme, sbaTransitions),
    stylesToString(theme, sbaLevels),
    theme === 'sap_horizon' ? horizonStyles : '',
    overrides ? stylesToString(theme, overrides) : '',
  ].join(' ');
  useStylesheet(combinedStyles, `${UI5ThemeProvider.displayName}-sba-${theme}`);

  return null;
}

export function ThemeProvider({ theme, children }: ThemeProviderProps) {
  useEffect(() => {
    const existingClasses = document.body.className.split(' ');
    document.body.className = existingClasses
      .filter((c) => !c.startsWith(stylePrefix))
      .concat(`${stylePrefix}${theme}`)
      .join(' ')
      .trim();

    setTheme(theme);
  }, [theme]);

  return (
    <UI5ThemeProvider>
      <OverriddenStyles theme={theme} />
      {children}
    </UI5ThemeProvider>
  );
}
